import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import {
  clearAuthHeader,
  clearClientHeader,
  clearTenantHeader,
  clearUserHeader,
  setAuthHeader,
  setE2ETransactionIdHeader,
  setClientHeader,
  setTenantHeader,
  setUserHeader,
} from '../../http/headers';
import { processAuthToken } from './AuthProvider/processAuthToken';
import type { Membership, Profile, Tenant } from './ClientsProvider/fetchTenantsData';
import { fetchTenantsData } from './ClientsProvider/fetchTenantsData';
import { fetchFreshness } from './ClientsProvider/fetchFreshness';
import type { Module } from './ClientsProvider/fetchModules';
import { fetchModules } from './ClientsProvider/fetchModules';
import { e2eLogin } from './e2eLogin';

export type CurrentUser = { email: string; token: string };

export type AppState = {
  appVersion: string;
  envVars: Record<string, unknown> | null;
  currentUser: CurrentUser | null;
  profile: Profile | null;
  memberships: Membership[];
  selectedMembership: Membership | null;
  tenants: Tenant[];
  selectedTenant: Tenant | null;
  modules: Module[];
  // Last day the tenant's actuals are synced through; null before the first
  // sync or when the lookup failed.
  actualsThrough: string | null;
  userAuthenticated: boolean;
  sessionLoaded: boolean;
  modulesLoaded: boolean;
  error: string | null;
  socketUrl: string | null;
  currentPath: string;
};

// Canonical localStorage key for the user's last-picked tenant. Re-exported via
// `@eo/shared` so host apps don't drift the literal across logout / interceptor /
// reducer code.
export const SELECTED_CLIENT_KEY = 'selectedClient';

// Same contract as SELECTED_CLIENT_KEY, for the parent tenant that scopes the
// portfolio views.
export const SELECTED_TENANT_KEY = 'selectedTenant';

// When a super-admin "enters" a tenant from the admin Clients page, they may
// have no membership row for it. We stash the client identity here so the boot
// chain can synthesize a `selectedMembership` (the backend lets super-admins
// act cross-tenant; only the frontend boot needs the bridge). Re-exported via
// `@eo/shared` so the host's enter-tenant helper writes the same key.
export const ADMIN_ENTERED_CLIENT_KEY = 'adminEnteredClient';

type AdminEnteredClient = {
  clientId: string;
  client: string;
  clientName: string;
  logoUrl?: string;
};

const initialPath = typeof window !== 'undefined' ? window.location.pathname : '/';

const initialState: AppState = {
  appVersion: '',
  envVars: null,
  currentUser: null,
  profile: null,
  memberships: [],
  selectedMembership: null,
  tenants: [],
  selectedTenant: null,
  modules: [],
  actualsThrough: null,
  userAuthenticated: false,
  sessionLoaded: false,
  modulesLoaded: false,
  error: null,
  socketUrl: null,
  currentPath: initialPath,
};

function readStoredClient(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SELECTED_CLIENT_KEY);
}

function writeStoredClient(client: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SELECTED_CLIENT_KEY, client);
}

function readStoredTenant(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SELECTED_TENANT_KEY);
}

function writeStoredTenant(tenant: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SELECTED_TENANT_KEY, tenant);
}

function readAdminEnteredClient(): AdminEnteredClient | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(ADMIN_ENTERED_CLIENT_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<AdminEnteredClient>;
    if (
      typeof v.clientId === 'string' &&
      typeof v.client === 'string' &&
      typeof v.clientName === 'string'
    ) {
      return {
        clientId: v.clientId,
        client: v.client,
        clientName: v.clientName,
        logoUrl: v.logoUrl,
      };
    }
  } catch {
    // malformed entry — ignore
  }
  return null;
}

function clearAdminEnteredClient(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(ADMIN_ENTERED_CLIENT_KEY);
}

type SelectMembershipPayload = string | { client: string; preserveUrl?: boolean };

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateCurrentPath(state, action: PayloadAction<string>) {
      state.currentPath = action.payload;
    },
    updateAppState(state, action: PayloadAction<Partial<AppState>>) {
      Object.assign(state, action.payload);
    },
    // Wipes auth-derived fields so re-auth re-runs the boot chain — resetting
    // `sessionLoaded` is the critical one; bootstrap-time host config survives.
    clearAuth(state) {
      state.currentUser = null;
      state.userAuthenticated = false;
      state.sessionLoaded = false;
      state.profile = null;
      state.memberships = [];
      state.selectedMembership = null;
      state.tenants = [];
      state.selectedTenant = null;
      state.modules = [];
      state.modulesLoaded = false;
      state.actualsThrough = null;
      state.error = null;
      clearAdminEnteredClient();
      clearAuthHeader();
      clearUserHeader();
      clearClientHeader();
      clearTenantHeader();
    },
    // Switching tenant is a hard reload — wipes in-flight requests and stale
    // per-page state in one move; soft switching would need AbortController
    // plumbing in every controller.
    selectMembership(state, action: PayloadAction<SelectMembershipPayload>) {
      const client = typeof action.payload === 'string' ? action.payload : action.payload.client;
      const preserveUrl =
        typeof action.payload === 'string' ? false : Boolean(action.payload.preserveUrl);
      writeStoredClient(client);
      setClientHeader(client);
      if (typeof window !== 'undefined') {
        if (!preserveUrl) {
          window.location.search = '';
        }
        window.location.reload();
      }
      // Local state updates below are moot once the reload kicks in, but they
      // keep the slice consistent for the brief window before navigation.
      const found = state.memberships.find((m) => m.client === client);
      if (found) {
        state.selectedMembership = found;
      }
    },
    // Hard reload for the same reason selectMembership does it: every portfolio
    // view is scoped by the X-Tenant header, so a soft switch would have to
    // cancel and refire each page's in-flight reads.
    selectTenant(state, action: PayloadAction<string>) {
      const slug = action.payload;
      writeStoredTenant(slug);
      setTenantHeader(slug);
      if (typeof window !== 'undefined') {
        window.location.search = '';
        window.location.reload();
      }
      const found = state.tenants.find((t) => t.slug === slug);
      if (found) {
        state.selectedTenant = found;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(processAuthToken.fulfilled, (state, { payload }) => {
      state.currentUser = { email: payload.email, token: payload.token };
      state.userAuthenticated = true;
    });

    builder.addCase(fetchTenantsData.fulfilled, (state, { payload }) => {
      state.profile = payload.profile;
      state.memberships = payload.memberships;
      const stored = readStoredClient();
      const storedMembership =
        (stored && payload.memberships.find((m) => m.client === stored)) || null;

      // Super-admin entered a tenant from the admin Clients page: no membership
      // row exists, so synthesize one from the stashed client identity (role
      // 'owner' — moot for super-admins, who bypass every per-tenant gate). It
      // is prepended to `memberships` so the tenant switcher reflects it.
      // Resolved BEFORE the single-membership fallback below: a super-admin who
      // happens to have exactly one membership would otherwise be auto-selected
      // back onto it, and the write-back at the end would overwrite the entered
      // slug in localStorage — so entering a client from admin looked ignored
      // and the switcher could never leave their own tenant.
      let synthesized: Membership | null = null;
      if (!storedMembership && payload.profile.isSuperAdmin) {
        const entered = readAdminEnteredClient();
        if (entered && entered.client === stored) {
          const membership: Membership = {
            clientId: entered.clientId,
            client: entered.client,
            clientName: entered.clientName,
            logoUrl: entered.logoUrl,
            role: 'owner',
          };
          synthesized = membership;
          state.memberships = [
            membership,
            ...payload.memberships.filter((m) => m.client !== membership.client),
          ];
        }
      }

      // Auto-select: N=0 → null (routes /no-client); N=1 → the only one;
      // N>1 → localStorage if still valid, else null.
      const matched: Membership | null =
        storedMembership ??
        synthesized ??
        (payload.memberships.length === 1 ? payload.memberships[0] : null);
      if (matched) {
        state.selectedMembership = matched;
        setClientHeader(matched.client);
        writeStoredClient(matched.client);
      }
      // Portfolio scope: the last-picked tenant if it is still visible to the
      // user, else the first one. The X-Tenant header drives which tenant the
      // portfolio reads/writes hit.
      state.tenants = payload.tenants ?? [];
      const storedTenant = readStoredTenant();
      state.selectedTenant =
        state.tenants.find((t) => t.slug === storedTenant) ?? state.tenants[0] ?? null;
      if (state.selectedTenant) {
        setTenantHeader(state.selectedTenant.slug);
        writeStoredTenant(state.selectedTenant.slug);
      } else {
        clearTenantHeader();
      }
      state.sessionLoaded = true;
    });

    builder.addCase(fetchModules.fulfilled, (state, { payload }) => {
      state.modules = payload;
      state.modulesLoaded = true;
    });
    builder.addCase(fetchFreshness.fulfilled, (state, { payload }) => {
      state.actualsThrough = payload;
    });
    // Never blocks boot: without a date the pickers fall back to their own
    // "not today" default rather than the tenant's real boundary.
    builder.addCase(fetchFreshness.rejected, (state) => {
      state.actualsThrough = null;
    });

    builder.addCase(fetchModules.rejected, (state) => {
      // Routes still render the admin shell; sidebar shows no module entries.
      state.modules = [];
      state.modulesLoaded = true;
    });

    builder.addCase(e2eLogin, (state, { payload }) => {
      state.currentUser = { email: payload.email, token: payload.token };
      state.userAuthenticated = true;
      setAuthHeader(payload.token);
      setUserHeader(payload.email);
      setE2ETransactionIdHeader(payload.transactionId);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(
          'e2e_auth_data',
          JSON.stringify({
            email: payload.email,
            token: payload.token,
            transactionId: payload.transactionId,
          }),
        );
      }
    });
  },
});

export const { updateCurrentPath, updateAppState, selectMembership, selectTenant, clearAuth } =
  slice.actions;
export const appReducer = slice.reducer;
export { processAuthToken } from './AuthProvider/processAuthToken';
export { fetchTenantsData } from './ClientsProvider/fetchTenantsData';
export { fetchFreshness } from './ClientsProvider/fetchFreshness';
export { fetchModules } from './ClientsProvider/fetchModules';
export { e2eLogin } from './e2eLogin';
export type {
  Membership,
  MembershipRole,
  Profile,
  Tenant,
} from './ClientsProvider/fetchTenantsData';
export type { Module } from './ClientsProvider/fetchModules';
